import { useEffect, useRef } from 'react'
import useQueueStore from '../stores/queueStore'
import usePlaybackStore from '../stores/playbackStore'

/**
 * Hook to manage preloading of song data for previous, current, and next songs
 * This ensures instant transitions by having song data ready before needed
 */
export const useSongDataPreloader = () => {
  const previousStateRef = useRef({
    previous: null,
    current: null,
    next: null
  })

  const queue = useQueueStore(state => state.queue)
  const { getPreviousSong, getCurrentSong, getNextSong } = useQueueStore.getState()
  const { cursor, currentSong, preloadedSongData, playbackVersion, getSongData } = usePlaybackStore()

  useEffect(() => {
    if (!queue.length) {
      // Clear any cached song data when queue empties
      const hasCachedData = preloadedSongData.previous || preloadedSongData.current || preloadedSongData.next
      if (hasCachedData) {
        usePlaybackStore.setState({
          preloadedSongData: {
            previous: null,
            current: null,
            next: null
          }
        })
      }
      previousStateRef.current = { previous: null, current: null, next: null }
      return
    }

    const preloadSongData = async () => {
      const startingVersion = playbackVersion
      // Get the 3 positions based on current cursor
      const prevItem = getPreviousSong(queue, cursor)
      const currentItem = getCurrentSong(queue, cursor)
      const nextItem = getNextSong(queue, cursor)

      // Current state for comparison
      const currentState = {
        previous: prevItem?.songId || null,
        current: currentItem?.songId || null,
        next: nextItem?.songId || null,
        version: startingVersion
      }

      const previousState = previousStateRef.current

      console.log('🎵 📦 Song data preloader check:', {
        cursor,
        currentState,
        previousState,
        queueLength: queue.length,
        version: startingVersion
      })

      // Align cached song data with current queue items to avoid stale entries
      const alignedPreloadedData = {
        previous: prevItem && preloadedSongData.previous?.id === prevItem.songId ? preloadedSongData.previous : null,
        current: currentItem && preloadedSongData.current?.id === currentItem.songId ? preloadedSongData.current : null,
        next: nextItem && preloadedSongData.next?.id === nextItem.songId ? preloadedSongData.next : null
      }

      const cacheChanged = (
        alignedPreloadedData.previous !== preloadedSongData.previous ||
        alignedPreloadedData.current !== preloadedSongData.current ||
        alignedPreloadedData.next !== preloadedSongData.next
      )

      if (cacheChanged) {
        usePlaybackStore.setState({ preloadedSongData: alignedPreloadedData })
      }

      // Track what needs preloading (only when not already aligned)
      const toPreload = []

      // Check if previous song changed
      if (prevItem && !alignedPreloadedData.previous) {
        console.log(`🎵 📦 Previous song changed, preloading: ${prevItem.song?.title}`)
        toPreload.push({
          type: 'previous',
          songId: prevItem.songId,
          item: prevItem
        })
      }

      // Check if current song changed
      if (currentItem && !alignedPreloadedData.current) {
        console.log(`🎵 📦 Current song changed, preloading: ${currentItem.song?.title}`)
        toPreload.push({
          type: 'current',
          songId: currentItem.songId,
          item: currentItem
        })
      }

      // Check if next song changed  
      if (nextItem && !alignedPreloadedData.next) {
        console.log(`🎵 📦 Next song changed, preloading: ${nextItem.song?.title}`)
        toPreload.push({
          type: 'next',
          songId: nextItem.songId,
          item: nextItem
        })
      }

      // Preload all changed songs in parallel
      if (toPreload.length > 0) {
        try {
          const preloadPromises = toPreload.map(async ({ type, songId, item }) => {
            try {
              const songData = await getSongData(songId)
              return { type, songData, success: true }
            } catch (error) {
              console.warn(`Failed to preload ${type} song (${item.song?.title}):`, error)
              return { type, songData: null, success: false }
            }
          })

          const results = await Promise.allSettled(preloadPromises)

          // Update preloaded song data with successful results
          const newPreloadedData = { ...alignedPreloadedData }
          let preloadedUpdated = false

          results.forEach((result, index) => {
            if (result.status === 'fulfilled' && result.value.success) {
              const { type, songData } = result.value
              newPreloadedData[type] = songData
              preloadedUpdated = true
              console.log(`🎵 ✅ Successfully preloaded ${type} song: ${songData.title}`)
            }
          })

          // Update store with new preloaded data
          if ((preloadedUpdated || cacheChanged) && playbackVersion === startingVersion) {
            usePlaybackStore.setState({ preloadedSongData: newPreloadedData })
          }

        } catch (error) {
          console.error('Error during song data preloading:', error)
        }
      } else if (cacheChanged) {
        // Cache already aligned and no new data needed
        console.log('🎵 📦 Preloaded song cache aligned with queue (no new loads needed)')
      }

      // Update reference for next comparison
      previousStateRef.current = currentState
    }

    preloadSongData()

  }, [queue, cursor, currentSong, playbackVersion]) // Retrigger when queue, cursor, currentSong, or version changes

  // Return current preloaded data for components to use
  return {
    preloadedSongData,
    isPreloaded: {
      previous: !!preloadedSongData.previous,
      current: !!preloadedSongData.current,
      next: !!preloadedSongData.next
    }
  }
}
